import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    PiArrowLeftBold,
    PiArrowRightBold,
    PiArrowsClockwiseBold,
    PiBuildingsBold,
    PiEnvelopeBold,
    PiMapPinBold,
    PiPhoneBold,
    PiAnchorBold,
    PiWarningCircleBold,
    PiCheckCircleFill,
    PiXCircleFill,
    PiNavigationArrowBold,
    PiHashBold,
    PiReceiptBold,
    PiFilePdfBold,
    PiDownloadSimpleBold,
    PiCurrencyDollarBold,
    PiCalendarCheckBold,
    PiPencilSimpleBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

const FALLBACK = '/img/favicon.png';

/* ─── Types ──────────────────────────────────────────────────── */
type Shipment = {
    id: number;
    order_number: string;
    supplier: string | null;
    stage: string;
    stage_label: string;
    stage_color: string;
    status: string;
    order_date: string | null;
    eta: string | null;
    invoice: string | null;
    total_usd: number | null;
    financial_payment_date: string | null;
    invoice_financial_date: string | null;
    supplier_balance_date: string | null;
    href: string;
};

type PaymentReceipt = {
    shipment_id: number;
    order_number: string;
    supplier: string | null;
    task_title: string;
    field_key: string;
    payment_date: string | null;
    file_path: string;
    file_url: string | null;
    file_name: string | null;
};

type ClientDetail = {
    id: number;
    name: string;
    legal_name: string | null;
    display_name: string | null;
    cnpj: string | null;
    cnae: string | null;
    email: string | null;
    phone: string | null;
    address: string | null;
    neighborhood: string | null;
    zip_code: string | null;
    city: string | null;
    state: string | null;
    logo_url: string | null;
    notes: string | null;
    active: boolean;
    parent_name: string | null;
    parent: { id: number; name: string } | null;
    units_count: number;
    shipments_count: number;
    created_at: string | null;
    all_shipments: Shipment[];
    recent_shipments: Shipment[];
    payment_receipts: PaymentReceipt[];
};

/* ─── Utils ───────────────────────────────────────────────────── */
function fmtDate(iso: string | null | undefined) {
    if (!iso) return null;
    return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtUsd(v: number | null | undefined) {
    if (v == null) return null;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
}

/* ─── Stage badge ────────────────────────────────────────────── */
const STAGE_COLOR: Record<string, string> = {
    primary: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-400',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400',
    warning: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400',
    danger:  'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400',
    info:    'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-400',
    gray:    'border-gray-200 bg-gray-100 text-gray-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400',
};

function StageBadge({ label, color }: { label: string; color: string }) {
    return (
        <span className={cn('rounded-full border px-2.5 py-0.5 text-[11px] font-black', STAGE_COLOR[color] ?? STAGE_COLOR.gray)}>
            {label}
        </span>
    );
}

/* ─── Info row ──────────────────────────────────────────────── */
function InfoRow({ icon, label, value, href }: {
    icon: React.ReactNode; label: string; value: string | null | undefined; href?: string;
}) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-3">
            <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-500">
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-zinc-600">{label}</p>
                {href ? (
                    <a href={href} className="text-sm font-medium text-amber-600 hover:underline dark:text-[#C9A84C]">{value}</a>
                ) : (
                    <p className="text-sm font-medium text-gray-800 dark:text-zinc-200">{value}</p>
                )}
            </div>
        </div>
    );
}

/* ─── Stat card ─────────────────────────────────────────────── */
function StatCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex size-12 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 text-gray-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-500">
                {icon}
            </div>
            <div>
                <p className="text-2xl font-black text-gray-900 dark:text-white">{value}</p>
                <p className="text-xs font-bold text-gray-400 dark:text-zinc-600">{label}</p>
            </div>
        </div>
    );
}

/* ─── Tab ────────────────────────────────────────────────────── */
type TabId = 'shipments' | 'payments';

function Tab({ id, active, label, count, icon, onClick }: {
    id: TabId; active: boolean; label: string; count?: number; icon: React.ReactNode; onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-bold transition',
                active
                    ? 'border-amber-500 text-amber-600 dark:border-[#C9A84C] dark:text-[#C9A84C]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300',
            )}
        >
            {icon}
            {label}
            {count !== undefined && (
                <span className={cn(
                    'rounded-full px-1.5 py-0.5 text-[10px] font-black',
                    active
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-500',
                )}>
                    {count}
                </span>
            )}
        </button>
    );
}

/* ─── Página ─────────────────────────────────────────────────── */
export default function ClientDetailPage() {
    const { id } = useParams<{ id: string }>();
    const [activeTab, setActiveTab] = useState<TabId>('shipments');
    const navigate = useNavigate();

    const { data, isLoading, error } = useQuery({
        queryKey: ['clients', id],
        enabled: !!id,
        queryFn: async () => {
            const { data: body } = await api.get(`/clients/${id}`);
            return (body.data ?? body) as ClientDetail;
        },
    });

    if (isLoading) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center">
                <div className="flex items-center gap-3 text-gray-400 dark:text-zinc-500">
                    <PiArrowsClockwiseBold className="size-5 animate-spin" />
                    <span className="text-sm">Carregando cliente…</span>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex min-h-[30vh] flex-col items-center justify-center gap-4">
                <PiWarningCircleBold className="size-10 text-red-400" />
                <p className="text-sm text-red-500">Não foi possível carregar os dados.</p>
                <Link to="/clients" className="text-sm text-gray-500 hover:underline">← Voltar</Link>
            </div>
        );
    }

    const addressParts = [
        data.address,
        data.neighborhood,
        data.city && data.state ? `${data.city} / ${data.state}` : (data.city ?? data.state),
        data.zip_code ? `CEP ${data.zip_code.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2')}` : null,
    ].filter(Boolean);
    const fullAddress = addressParts.join(', ');
    const mapsUrl = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;

    const allShipments = data.all_shipments ?? [];
    const paymentReceipts = data.payment_receipts ?? [];

    return (
        <div className="space-y-0">

            {/* ── Breadcrumb ───────────────────────────────── */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3 dark:border-zinc-800/60">
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-zinc-600">
                    <Link to="/" className="hover:text-gray-600 dark:hover:text-zinc-400">Dashboard</Link>
                    <span>/</span>
                    <Link to="/clients" className="hover:text-gray-600 dark:hover:text-zinc-400">Clientes</Link>
                    <span>/</span>
                    <span className="text-gray-600 dark:text-zinc-400">{data.name}</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => navigate(`/clients/${id}/edit`)}
                        className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-600 transition hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400 dark:hover:bg-amber-500/20"
                    >
                        <PiPencilSimpleBold className="size-3.5" />
                        Editar
                    </button>
                    <Link
                        to="/clients"
                        className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-500 transition hover:bg-gray-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-zinc-700"
                    >
                        <PiArrowLeftBold className="size-3.5" />
                        Voltar
                    </Link>
                </div>
            </div>

            {/* ── Hero ─────────────────────────────────────── */}
            <div className="border-b border-gray-200 bg-gray-50/60 px-6 py-6 dark:border-zinc-800/60 dark:bg-zinc-900/40">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">

                    {/* Logo */}
                    <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
                        <img
                            src={data.logo_url || FALLBACK}
                            alt={data.name}
                            className="size-full object-contain p-2"
                            onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
                        />
                    </div>

                    {/* Identidade */}
                    <div className="min-w-0 flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            {data.active ? (
                                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-black text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">
                                    <PiCheckCircleFill className="size-3" />Ativo
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2.5 py-0.5 text-[11px] font-black text-gray-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-500">
                                    <PiXCircleFill className="size-3" />Inativo
                                </span>
                            )}
                            {data.parent_name && (
                                <span className="rounded-full border border-gray-200 bg-gray-100 px-2.5 py-0.5 text-[11px] text-gray-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400">
                                    Franquia de {data.parent_name}
                                </span>
                            )}
                        </div>
                        <h1 className="text-2xl font-black text-gray-900 dark:text-white">{data.name}</h1>
                        {data.legal_name && data.legal_name !== data.name && (
                            <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">{data.legal_name}</p>
                        )}
                        {data.cnpj && (
                            <p className="mt-1 font-mono text-xs text-gray-400 dark:text-zinc-600">{data.cnpj}</p>
                        )}
                    </div>

                    {/* Stats */}
                    <div className="flex shrink-0 gap-3">
                        <StatCard label="Embarques" value={data.shipments_count ?? 0} icon={<PiAnchorBold className="size-5" />} />
                        {paymentReceipts.length > 0 && (
                            <StatCard label="Comprovantes" value={paymentReceipts.length} icon={<PiReceiptBold className="size-5" />} />
                        )}
                        {(data.units_count ?? 0) > 0 && (
                            <StatCard label="Unidades" value={data.units_count} icon={<PiBuildingsBold className="size-5" />} />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Corpo ────────────────────────────────────── */}
            <div className="grid gap-5 px-6 py-6 lg:grid-cols-3">

                {/* Coluna esquerda */}
                <div className="flex flex-col gap-5">

                    {/* Contato */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
                        <p className="mb-4 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Contato</p>
                        <div className="flex flex-col gap-3">
                            <InfoRow icon={<PiEnvelopeBold className="size-3.5" />} label="E-mail" value={data.email} href={data.email ? `mailto:${data.email}` : undefined} />
                            <InfoRow icon={<PiPhoneBold className="size-3.5" />} label="Telefone" value={data.phone} href={data.phone ? `tel:${data.phone}` : undefined} />
                            {data.cnae && (
                                <InfoRow icon={<PiHashBold className="size-3.5" />} label="CNAE" value={data.cnae} />
                            )}
                        </div>
                    </div>

                    {/* Endereço */}
                    {addressParts.length > 0 && (
                        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
                            <div className="flex items-center justify-between px-5 pt-5 pb-3">
                                <div className="flex items-center gap-2">
                                    <PiMapPinBold className="size-4 text-gray-400 dark:text-zinc-500" />
                                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Endereço</p>
                                </div>
                                {mapsUrl && (
                                    <a
                                        href={mapsUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] font-bold text-amber-600 transition hover:bg-amber-50 dark:border-zinc-700 dark:text-[#C9A84C] dark:hover:bg-zinc-800"
                                    >
                                        <PiNavigationArrowBold className="size-3" />
                                        Ver no mapa
                                    </a>
                                )}
                            </div>
                            <div className="flex flex-col gap-2 px-5 pb-4">
                                {data.address && (
                                    <div>
                                        <p className="text-[10px] font-bold uppercase text-gray-400 dark:text-zinc-600">Logradouro</p>
                                        <p className="text-sm text-gray-700 dark:text-zinc-300">{data.address}</p>
                                    </div>
                                )}
                                {data.neighborhood && (
                                    <div>
                                        <p className="text-[10px] font-bold uppercase text-gray-400 dark:text-zinc-600">Bairro</p>
                                        <p className="text-sm text-gray-700 dark:text-zinc-300">{data.neighborhood}</p>
                                    </div>
                                )}
                                <div className="flex gap-4">
                                    {(data.city || data.state) && (
                                        <div className="flex-1">
                                            <p className="text-[10px] font-bold uppercase text-gray-400 dark:text-zinc-600">Cidade / UF</p>
                                            <p className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                                                {[data.city, data.state].filter(Boolean).join(' / ')}
                                            </p>
                                        </div>
                                    )}
                                    {data.zip_code && (
                                        <div>
                                            <p className="text-[10px] font-bold uppercase text-gray-400 dark:text-zinc-600">CEP</p>
                                            <p className="font-mono text-sm text-gray-700 dark:text-zinc-300">
                                                {data.zip_code.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {mapsUrl && (
                                <div className="border-t border-gray-100 dark:border-zinc-800">
                                    <iframe
                                        title="Mapa"
                                        className="h-44 w-full"
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        src={`https://maps.google.com/maps?q=${encodeURIComponent(fullAddress)}&output=embed&z=15`}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Observações */}
                    {data.notes && (
                        <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
                            <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">Observações</p>
                            <p className="text-sm leading-relaxed text-gray-700 dark:text-zinc-300">{data.notes}</p>
                        </div>
                    )}

                    {data.created_at && (
                        <p className="text-xs text-gray-400 dark:text-zinc-600">
                            Cadastrado em {fmtDate(data.created_at)}
                        </p>
                    )}
                </div>

                {/* Coluna direita: abas Embarques / Pagamentos */}
                <div className="lg:col-span-2">
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">

                        {/* Tabs */}
                        <div className="flex border-b border-gray-100 dark:border-zinc-800">
                            <Tab
                                id="shipments"
                                active={activeTab === 'shipments'}
                                label="Embarques"
                                count={allShipments.length}
                                icon={<PiAnchorBold className="size-4" />}
                                onClick={() => setActiveTab('shipments')}
                            />
                            <Tab
                                id="payments"
                                active={activeTab === 'payments'}
                                label="Comprovantes de pagamento"
                                count={paymentReceipts.length}
                                icon={<PiReceiptBold className="size-4" />}
                                onClick={() => setActiveTab('payments')}
                            />
                        </div>

                        {/* ── Tab: Embarques ─────────────────────── */}
                        {activeTab === 'shipments' && (
                            <>
                                {allShipments.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-14 text-gray-400 dark:text-zinc-600">
                                        <PiAnchorBold className="mb-2 size-8 opacity-30" />
                                        <p className="text-sm">Nenhum embarque registrado</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-gray-100 text-[10px] font-black uppercase tracking-wider text-gray-400 dark:border-zinc-800 dark:text-zinc-600">
                                                    <th className="px-4 py-3 text-left">Pedido</th>
                                                    <th className="px-4 py-3 text-left">Fornecedor</th>
                                                    <th className="px-4 py-3 text-left">Estágio</th>
                                                    <th className="px-4 py-3 text-right">Total USD</th>
                                                    <th className="px-4 py-3 text-center">1º Pgto</th>
                                                    <th className="px-4 py-3 text-center">2º Pgto</th>
                                                    <th className="px-4 py-3 text-center">ETA</th>
                                                    <th className="px-4 py-3"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800/60">
                                                {allShipments.map((s) => (
                                                    <tr
                                                        key={s.id}
                                                        className="cursor-pointer transition hover:bg-gray-50 dark:hover:bg-zinc-800/40"
                                                        onClick={() => window.location.href = s.href}
                                                    >
                                                        <td className="px-4 py-3">
                                                            <span className="font-mono text-xs font-black text-gray-800 dark:text-zinc-200">{s.order_number}</span>
                                                            {s.order_date && (
                                                                <p className="text-[10px] text-gray-400 dark:text-zinc-600">{fmtDate(s.order_date)}</p>
                                                            )}
                                                        </td>
                                                        <td className="max-w-[140px] px-4 py-3">
                                                            <span className="truncate text-xs text-gray-600 dark:text-zinc-400">{s.supplier ?? '—'}</span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <StageBadge label={s.stage_label} color={s.stage_color} />
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            {s.total_usd ? (
                                                                <span className="font-mono text-xs font-bold text-gray-700 dark:text-zinc-300">{fmtUsd(s.total_usd)}</span>
                                                            ) : <span className="text-gray-300 dark:text-zinc-700">—</span>}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {s.financial_payment_date ? (
                                                                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
                                                                    <PiCalendarCheckBold className="size-3" />
                                                                    {fmtDate(s.financial_payment_date)}
                                                                </span>
                                                            ) : <span className="text-[11px] text-gray-300 dark:text-zinc-700">—</span>}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {s.supplier_balance_date ? (
                                                                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
                                                                    <PiCalendarCheckBold className="size-3" />
                                                                    {fmtDate(s.supplier_balance_date)}
                                                                </span>
                                                            ) : <span className="text-[11px] text-gray-300 dark:text-zinc-700">—</span>}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className="text-[11px] text-gray-500 dark:text-zinc-500">{fmtDate(s.eta) ?? '—'}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <PiArrowRightBold className="inline size-4 text-gray-300 dark:text-zinc-700" />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}

                        {/* ── Tab: Comprovantes ──────────────────── */}
                        {activeTab === 'payments' && (
                            <>
                                {paymentReceipts.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-14 text-gray-400 dark:text-zinc-600">
                                        <PiReceiptBold className="mb-2 size-8 opacity-30" />
                                        <p className="text-sm">Nenhum comprovante encontrado</p>
                                        <p className="mt-1 text-xs">Comprovantes anexados nas tarefas de pagamento aparecem aqui</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-100 dark:divide-zinc-800/60">
                                        {paymentReceipts.map((r, idx) => (
                                            <div key={idx} className="flex items-center gap-4 px-5 py-4">
                                                {/* Ícone */}
                                                <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800">
                                                    <PiFilePdfBold className="size-5 text-red-400" />
                                                </div>

                                                {/* Info */}
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono text-xs font-black text-gray-700 dark:text-zinc-200">{r.order_number}</span>
                                                        <span className="text-gray-300 dark:text-zinc-700">·</span>
                                                        <span className="text-xs text-gray-500 dark:text-zinc-400">{r.supplier}</span>
                                                    </div>
                                                    <p className="mt-0.5 truncate text-xs text-gray-400 dark:text-zinc-500">{r.task_title}</p>
                                                    {r.file_name && (
                                                        <p className="mt-0.5 truncate font-mono text-[10px] text-gray-400 dark:text-zinc-600">{r.file_name}</p>
                                                    )}
                                                </div>

                                                {/* Data */}
                                                {r.payment_date && (
                                                    <div className="shrink-0 text-right">
                                                        <p className="text-[10px] font-bold uppercase text-gray-400 dark:text-zinc-600">Pago em</p>
                                                        <div className="flex items-center gap-1 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                                                            <PiCurrencyDollarBold className="size-3" />
                                                            {fmtDate(r.payment_date)}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Download */}
                                                {r.file_url && (
                                                    <a
                                                        href={r.file_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-500 transition hover:border-amber-300 hover:text-amber-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-amber-500/50 dark:hover:text-[#C9A84C]"
                                                    >
                                                        <PiDownloadSimpleBold className="size-3.5" />
                                                        Abrir
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
